/**
 * The outbox sync engine.
 *
 * Lifecycle of one entry:
 *
 *   enqueue → pending ──drain──▶ in_flight ──POST──▶ ┬ applied/duplicate → succeeded
 *                                                    ├ conflict → policy → discard|retry|manual
 *                                                    └ rejected (4xx) → failed (+ onConflict event)
 *
 *   transient failure (network / 5xx) → back to pending, attempts++, backoff
 *
 * Idempotency: every entry carries a UUID v7 `client_event_uuid` minted once
 * at enqueue and reused on every retry. A batch that is sent, succeeds
 * server-side, but whose response is lost (tab closed mid-flight) is safely
 * replayed on next start — the server recognises the duplicate key and returns
 * its prior verdict instead of double-applying. This is the property the
 * adversarial tests assert: total server-applied set == enqueued set, no loss,
 * no dup.
 */

import { nextBackoffMs } from './backoff'
import { resolveConflictPolicy } from './conflict-policy'
import { MemoryOutboxStore } from './memory-store'
import { OnlineDetector } from './online-detector'
import { registerBackgroundSync } from './service-worker'
import type {
	BatchItem,
	BatchResponse,
	ConflictPolicy,
	EnqueueInput,
	FetchImpl,
	OutboxEntry,
	OutboxStore,
	ProgressSnapshot,
	SendBatch,
	Serializer,
	SyncConfirmation,
	SyncConflict,
	SyncQueueConfig,
} from './types'
import { uuidv7 } from './uuid-v7'
import {
	validateEnqueueInput,
	validateListPendingArgs,
	validateResolveConflictArgs,
	validateSyncQueueConfig,
	validateTransportResponse,
} from './validate-input'

const IDENTITY_SERIALIZER: Serializer = { serialize: (e) => e.payload }

/**
 * Detach a timer from the event loop so a library-internal interval/timeout
 * never keeps the host process (Node, Bun, a test runner) alive on its own.
 * In the browser the timer id is a `number` and has no `unref`; this is then
 * a safe no-op.
 */
function unrefTimer(handle: unknown): void {
	if (
		handle &&
		typeof handle === 'object' &&
		'unref' in handle &&
		typeof (handle as { unref: unknown }).unref === 'function'
	) {
		;(handle as { unref: () => void }).unref()
	}
}

export class SyncQueue {
	private readonly store: OutboxStore
	private readonly endpoint: string
	private readonly batchSize: number
	private readonly fetchImpl: FetchImpl
	private readonly transport: SendBatch | null
	private readonly serializer: Serializer
	private readonly policy: ConflictPolicy
	private readonly maxAttempts: number
	private readonly backoffBaseMs: number
	private readonly backoffMaxMs: number
	private readonly pollIntervalMs: number
	private readonly outboxTable: string
	private readonly headers: Record<string, string>
	private readonly cfg: SyncQueueConfig
	private readonly detector: OnlineDetector

	private running = false
	private draining = false
	private pollTimer: ReturnType<typeof setTimeout> | null = null
	private backoffUntil = 0
	private maxAttemptsObserved = 0
	private visibilityBound = false
	private readonly conflicts: SyncConflict[] = []
	private readonly conflictListeners = new Set<(c: SyncConflict[]) => void>()
	private readonly confirmationListeners = new Set<
		(c: SyncConfirmation) => void
	>()

	constructor(config: SyncQueueConfig) {
		// Wave 10 zod-mandate — runtime validation at the public boundary.
		validateSyncQueueConfig(config)
		this.cfg = config
		this.store = config.store ?? new MemoryOutboxStore()
		this.endpoint = config.endpoint
		this.batchSize = config.batchSize ?? 25
		this.fetchImpl =
			config.fetchImpl ??
			((input, init) =>
				(globalThis as unknown as { fetch: FetchImpl }).fetch(input, init))
		this.transport = config.transport ?? null
		this.serializer = config.serializer ?? IDENTITY_SERIALIZER
		this.policy = resolveConflictPolicy(config.conflictPolicy)
		this.maxAttempts = config.maxAttempts ?? 16
		this.backoffBaseMs = config.backoffBaseMs ?? 1000
		this.backoffMaxMs = config.backoffMaxMs ?? 3_600_000
		this.pollIntervalMs = config.pollIntervalMs ?? 5000
		this.outboxTable = config.outboxTable ?? 'outbox'
		this.headers = config.headers ?? {}
		this.detector = new OnlineDetector({
			heartbeatUrl: config.heartbeatUrl ?? config.endpoint,
			heartbeatIntervalMs: config.heartbeatIntervalMs ?? 30_000,
			fetchImpl: this.fetchImpl,
			onChange: (online) => this.onConnectivityChange(online),
		})
	}

	// ---- public API --------------------------------------------------------

	/**
	 * Durably record a local mutation. Returns the assigned outbox id. Triggers
	 * a drain attempt if the queue is running and online.
	 */
	async enqueue(input: EnqueueInput): Promise<number> {
		// Wave 10 zod-mandate — runtime validation at the public boundary.
		validateEnqueueInput(input)
		const now = Date.now()
		const entry: Omit<OutboxEntry, 'id'> = {
			table: input.table,
			operation: input.operation,
			payload: input.payload,
			attempts: 0,
			last_attempt_at: null,
			status: 'pending',
			client_event_uuid: input.clientEventUuid ?? uuidv7(now),
			created_at: now,
			base_version: input.baseVersion ?? null,
			last_error: null,
			// The host's optimistic placeholder key — a durable column, so the
			// confirmation of a create enqueued in a PREVIOUS session is still
			// reconcilable after cold-start replay. Never serialized to the wire.
			temp_id: input.tempId ?? null,
		}
		const id = await this.store.add(entry)
		await this.emitProgress()
		if (this.running && this.detector.online) void this.flushNow()
		return id
	}

	/** Start connectivity detection, recover in-flight entries, begin draining. */
	async start(): Promise<void> {
		if (this.running) return
		this.running = true
		this.detector.start()
		this.bindVisibility()
		// Cold-start recovery: any entry left `in_flight` from a previous session
		// (tab closed mid-batch) is reset to `pending`. Idempotency keys make the
		// replay safe even if the server already applied it.
		const stuck = await this.store.listInFlight()
		for (const e of stuck) {
			await this.store.update(e.id, { status: 'pending' })
		}
		await this.emitProgress()
		if (this.detector.online) void this.flushNow()
		this.scheduleNextPoll()
	}

	/** Stop draining, timers, and connectivity listeners. */
	stop(): void {
		this.running = false
		this.detector.stop()
		this.unbindVisibility()
		if (this.pollTimer !== null) {
			clearTimeout(this.pollTimer)
			this.pollTimer = null
		}
	}

	/**
	 * Drain every pending entry now (respecting the active backoff window).
	 * Safe to call concurrently — re-entrancy is guarded.
	 */
	async flushNow(): Promise<void> {
		if (this.draining || !this.running) return
		if (!this.detector.online) return
		if (Date.now() < this.backoffUntil) return
		this.draining = true
		try {
			let batch = await this.eligibleBatch()
			while (batch.length > 0 && this.running && this.detector.online) {
				const ok = await this.sendBatch(batch)
				if (!ok) break // transient failure → backoff already scheduled
				batch = await this.eligibleBatch()
			}
		} finally {
			this.draining = false
		}
	}

	/**
	 * Register the service worker and request a Background Sync. When the SW +
	 * Background Sync API are present the browser will replay the outbox even
	 * after the tab closes; otherwise the in-page poll loop is the fallback
	 * (already active via {@link start}).
	 */
	async registerServiceWorker(): Promise<boolean> {
		if (!this.cfg.serviceWorkerUrl) return false
		return registerBackgroundSync(
			this.cfg.serviceWorkerUrl,
			this.cfg.backgroundSyncTag ?? 'norkron-sync-queue',
		)
	}

	/** Subscribe to the live conflict list (for {@link useConflicts}). */
	subscribeConflicts(listener: (c: SyncConflict[]) => void): () => void {
		this.conflictListeners.add(listener)
		listener([...this.conflicts])
		return () => this.conflictListeners.delete(listener)
	}

	/**
	 * Subscribe to per-item server confirmations — the multi-listener form of
	 * {@link SyncQueueConfig.onConfirmation}. Returns an unsubscribe function.
	 *
	 * Confirmations are an ephemeral event stream, not a retained list (unlike
	 * conflicts, which await resolution): a listener added after an item confirmed
	 * does not see it. Subscribe before {@link start}, or read the durable record
	 * via the entry's `succeeded` status.
	 *
	 * This is the seam that makes a consumer-side confirmation bus unnecessary —
	 * every part of the app that folds server truth into state (a temp-id
	 * registry, a screen's row list, a telemetry sink) attaches here instead of
	 * sharing one `onConfirmation` slot.
	 */
	subscribeConfirmations(listener: (c: SyncConfirmation) => void): () => void {
		this.confirmationListeners.add(listener)
		return () => this.confirmationListeners.delete(listener)
	}

	/** Current unresolved conflicts (manual policy). */
	getConflicts(): SyncConflict[] {
		return [...this.conflicts]
	}

	/**
	 * Resolve a manual conflict: `keep` re-enqueues the local mutation rebased
	 * on the server version; `discard` drops it. Removes it from the list.
	 */
	async resolveConflict(
		clientEventUuid: string,
		decision: 'keep' | 'discard',
	): Promise<void> {
		// Wave 10 zod-mandate — runtime validation at the public boundary.
		validateResolveConflictArgs(clientEventUuid, decision)
		const idx = this.conflicts.findIndex(
			(c) => c.entry.client_event_uuid === clientEventUuid,
		)
		if (idx < 0) return
		const [conflict] = this.conflicts.splice(idx, 1)
		if (!conflict) return
		if (decision === 'keep') {
			await this.store.update(conflict.entry.id, {
				status: 'pending',
				base_version: conflict.serverVersion,
				last_error: null,
			})
		} else {
			await this.store.update(conflict.entry.id, { status: 'failed' })
		}
		this.notifyConflicts()
		await this.emitProgress()
		if (decision === 'keep' && this.running && this.detector.online) {
			void this.flushNow()
		}
	}

	/** Snapshot of queue counts. */
	getProgress(): Promise<ProgressSnapshot> {
		return this.store.counts()
	}

	/**
	 * Read up to `limit` `pending` outbox entries in FIFO (ascending-id) order —
	 * the same order the drain sends them. Omit `limit` for every pending entry.
	 *
	 * This is the entry-level counterpart to {@link getProgress} (which returns
	 * counts only): it exposes the queue's own pending set so a host can fold the
	 * unsynced mutations over server state to render an optimistic overlay,
	 * without holding a separate reference to the underlying store. The reactive
	 * React binding is {@link useOutboxEntries}.
	 */
	listPending(limit?: number): Promise<OutboxEntry[]> {
		validateListPendingArgs(limit)
		return this.store.listPending(limit ?? Number.MAX_SAFE_INTEGER)
	}

	/**
	 * Hard-delete every `succeeded` row from the backing store and return the
	 * number pruned. Succeeded entries otherwise linger — on confirm they only
	 * leave the pending set — so a long-lived queue accumulates them. Call this
	 * after the host has folded a confirmed mutation into its authoritative state
	 * to reclaim the row.
	 *
	 * Explicit + host-invoked by design: the drain never prunes automatically, so
	 * a host that wants to inspect succeeded entries (audit, telemetry) keeps
	 * full control of retention. Pending, `in_flight`, and `failed` rows are
	 * untouched. Mirrors {@link getProgress} in delegating straight to the store.
	 */
	pruneSucceeded(): Promise<number> {
		return this.store.pruneSucceeded()
	}

	/** Current composite online state. */
	get isOnline(): boolean {
		return this.detector.online
	}

	// ---- internal ----------------------------------------------------------

	/**
	 * Next batch of pending entries, with the attempt-cap circuit-breaker
	 * applied: any entry that has already exhausted `maxAttempts` is retired to
	 * `failed` (with a conflict event) instead of being sent again. This is the
	 * guard that makes an unresolvable conflict or a permanently-broken server
	 * terminate the drain rather than spin forever.
	 */
	private async eligibleBatch(): Promise<OutboxEntry[]> {
		const candidates = await this.store.listPending(this.batchSize)
		if (this.maxAttempts <= 0) return candidates
		const eligible: OutboxEntry[] = []
		for (const e of candidates) {
			if (e.attempts >= this.maxAttempts) {
				await this.store.update(e.id, {
					status: 'failed',
					last_error:
						e.last_error ?? `exceeded maxAttempts (${this.maxAttempts})`,
				})
				this.pushConflict({
					entry: { ...e, status: 'failed' },
					serverRow: null,
					serverVersion: null,
					message: `exceeded maxAttempts (${this.maxAttempts})`,
					detected_at: Date.now(),
				})
			} else {
				eligible.push(e)
			}
		}
		if (eligible.length === 0 && candidates.length > 0) await this.emitProgress()
		return eligible
	}

	private async sendBatch(entries: OutboxEntry[]): Promise<boolean> {
		const ids = entries.map((e) => e.id)
		const attemptAt = Date.now()
		for (const e of entries) {
			await this.store.update(e.id, {
				status: 'in_flight',
				last_attempt_at: attemptAt,
			})
		}
		await this.emitProgress()

		// Custom transport seam: drain this group through the consumer's sender
		// (e.g. per-entry REST) instead of the built-in batch POST. The engine
		// still owns the lifecycle — applyResults runs identically on the returned
		// per-entry verdicts; a throw is a transient failure (offline / 5xx).
		if (this.transport) {
			let body: BatchResponse
			try {
				body = await this.transport(entries)
			} catch (err) {
				await this.handleTransientFailure(entries, String(err))
				return false
			}
			// The one seam where consumer code supplies the engine with a wire-shaped
			// value. Warn-only: `applyResults` is already total over a malformed
			// response, so this reports an off-contract sender without altering the
			// drain (see validateTransportResponse).
			validateTransportResponse(body)
			await this.applyResults(entries, body, ids)
			return true
		}

		const items: BatchItem[] = entries.map((e) => ({
			client_event_uuid: e.client_event_uuid,
			table: e.table,
			operation: e.operation,
			payload: this.serializer.serialize(e),
			base_version: e.base_version,
		}))

		let response: Response
		try {
			response = await this.fetchImpl(this.endpoint, {
				method: 'POST',
				headers: { 'content-type': 'application/json', ...this.headers },
				body: JSON.stringify({ outbox_table: this.outboxTable, items }),
			})
		} catch (err) {
			await this.handleTransientFailure(entries, String(err))
			return false
		}

		if (response.status >= 500 || response.status === 429) {
			await this.handleTransientFailure(
				entries,
				`server ${response.status}`,
			)
			return false
		}

		if (response.status >= 400 && response.status !== 409) {
			// Hard client error (400/401/403/404/422...): the request itself is
			// malformed/forbidden — retrying is futile. Mark failed + event.
			let message = `client error ${response.status}`
			try {
				message = (await response.text()) || message
			} catch {
				/* ignore body read failure */
			}
			for (const e of entries) {
				await this.store.update(e.id, {
					status: 'failed',
					attempts: e.attempts + 1,
					last_error: message,
				})
				this.pushConflict({
					entry: { ...e, status: 'failed' },
					serverRow: null,
					serverVersion: null,
					message,
					detected_at: Date.now(),
				})
			}
			await this.emitProgress()
			return true // not transient; continue draining the rest
		}

		let body: BatchResponse
		try {
			body = (await response.json()) as BatchResponse
		} catch (err) {
			// Got a 2xx/409 but unreadable body — treat as transient so the
			// idempotency key lets us safely replay.
			await this.handleTransientFailure(entries, `invalid response: ${err}`)
			return false
		}

		await this.applyResults(entries, body, ids)
		return true
	}

	private async applyResults(
		entries: OutboxEntry[],
		body: BatchResponse,
		_ids: number[],
	): Promise<void> {
		const byUuid = new Map(entries.map((e) => [e.client_event_uuid, e]))
		const seen = new Set<string>()
		for (const r of body.results ?? []) {
			const entry = byUuid.get(r.client_event_uuid)
			if (!entry) continue
			// A response that repeats a `client_event_uuid` must not fold twice —
			// it would double-count `attempts` and, since 0.4.0, emit a duplicate
			// confirmation. First verdict per key wins; the rest are ignored.
			if (seen.has(r.client_event_uuid)) continue
			seen.add(r.client_event_uuid)
			if (r.status === 'applied' || r.status === 'duplicate') {
				await this.store.update(entry.id, {
					status: 'succeeded',
					attempts: entry.attempts + 1,
					last_error: null,
				})
				// The server row is in hand right here — hand it to the host rather
				// than making it re-derive the read by wrapping `fetchImpl`. Reached
				// from BOTH send paths (built-in batch POST and custom transport),
				// because both funnel their verdicts through this method.
				this.emitConfirmation({
					...r,
					status: r.status,
					entry,
					table: entry.table,
					operation: entry.operation,
					...(entry.temp_id != null ? { tempId: entry.temp_id } : {}),
					serverRow: r.server_row ?? null,
					serverVersion: r.server_version ?? null,
					confirmed_at: Date.now(),
				})
			} else if (r.status === 'conflict') {
				await this.handleConflict(entry, r.server_row, r.server_version, r.message)
			} else {
				// 'rejected'
				await this.store.update(entry.id, {
					status: 'failed',
					attempts: entry.attempts + 1,
					last_error: r.message ?? 'rejected',
				})
				this.pushConflict({
					entry: { ...entry, status: 'failed' },
					serverRow: r.server_row ?? null,
					serverVersion: r.server_version ?? null,
					message: r.message ?? 'rejected',
					detected_at: Date.now(),
				})
			}
		}
		// Any entry the server did not mention is treated as not-yet-applied:
		// reset to pending so the idempotency key drives a safe replay.
		for (const e of entries) {
			if (!seen.has(e.client_event_uuid)) {
				await this.store.update(e.id, { status: 'pending' })
			}
		}
		await this.emitProgress()
	}

	private async handleConflict(
		entry: OutboxEntry,
		serverRow: unknown,
		serverVersion: number | undefined,
		message: string | undefined,
	): Promise<void> {
		const conflict: SyncConflict = {
			entry,
			serverRow: serverRow ?? null,
			serverVersion: serverVersion ?? null,
			message: message ?? null,
			detected_at: Date.now(),
			// Attached before the policy runs, so a custom `ConflictPolicy` can also
			// branch on whether this was an optimistic create.
			...(entry.temp_id != null ? { tempId: entry.temp_id } : {}),
		}
		const resolution = this.policy(conflict)
		if (resolution.action === 'discard') {
			await this.store.update(entry.id, {
				status: 'failed',
				attempts: entry.attempts + 1,
				last_error: message ?? 'conflict (discarded by policy)',
			})
		} else if (resolution.action === 'retry') {
			await this.store.update(entry.id, {
				status: 'pending',
				attempts: entry.attempts + 1,
				base_version:
					resolution.rebasedVersion ?? serverVersion ?? entry.base_version,
				last_error: null,
			})
		} else {
			// manual
			await this.store.update(entry.id, {
				status: 'failed',
				attempts: entry.attempts + 1,
				last_error: message ?? 'conflict (awaiting manual resolution)',
			})
			this.pushConflict(conflict)
		}
	}

	private async handleTransientFailure(
		entries: OutboxEntry[],
		reason: string,
	): Promise<void> {
		let maxAttempts = this.maxAttemptsObserved
		for (const e of entries) {
			const attempts = e.attempts + 1
			maxAttempts = Math.max(maxAttempts, attempts)
			await this.store.update(e.id, {
				status: 'pending',
				attempts,
				last_error: reason,
			})
		}
		this.maxAttemptsObserved = maxAttempts
		const delay = nextBackoffMs(maxAttempts, {
			baseMs: this.backoffBaseMs,
			maxMs: this.backoffMaxMs,
		})
		this.backoffUntil = Date.now() + delay
		await this.emitProgress()
		this.scheduleNextPoll(delay)
	}

	private onConnectivityChange(online: boolean): void {
		if (online) {
			// Connection regained → reset backoff so we retry immediately
			// instead of waiting out a long capped delay.
			this.backoffUntil = 0
			this.maxAttemptsObserved = 0
			if (this.running) void this.flushNow()
		}
	}

	private scheduleNextPoll(delayMs?: number): void {
		if (!this.running) return
		if (this.pollTimer !== null) clearTimeout(this.pollTimer)
		const wait = delayMs ?? this.pollIntervalMs
		this.pollTimer = setTimeout(() => {
			this.pollTimer = null
			void this.flushNow().finally(() => this.scheduleNextPoll())
		}, wait)
		// Library-internal timers must never keep the host process (or a test
		// runner) alive on their own — the consumer drives the lifecycle via
		// start()/stop(). `unref` is a no-op in browsers (timer id is a number).
		unrefTimer(this.pollTimer)
	}

	/**
	 * Fan a confirmation out to the config callback and every subscriber.
	 *
	 * Every invocation is isolated: a host listener that throws is swallowed so it
	 * cannot abort the drain mid-batch (which would leave entries `in_flight` and
	 * force a cold-start recovery). One bad listener also must not starve the
	 * others, hence the per-listener try.
	 */
	private emitConfirmation(c: SyncConfirmation): void {
		try {
			this.cfg.onConfirmation?.(c)
		} catch {
			/* a host callback must never break the outbox */
		}
		for (const l of this.confirmationListeners) {
			try {
				l(c)
			} catch {
				/* ditto — and one throwing listener must not starve the rest */
			}
		}
	}

	private pushConflict(c: SyncConflict): void {
		// Carry the host's placeholder key onto the conflict as well, so an
		// optimistic create that did NOT confirm is identifiable by the same token
		// as one that did (`SyncConfirmation.tempId`) — one mechanism, both
		// outcomes. Set only when the enqueue supplied one.
		const withTemp: SyncConflict =
			c.tempId === undefined && c.entry.temp_id != null
				? { ...c, tempId: c.entry.temp_id }
				: c
		this.conflicts.push(withTemp)
		this.cfg.onConflict?.(withTemp)
		this.notifyConflicts()
	}

	private notifyConflicts(): void {
		const snap = [...this.conflicts]
		for (const l of this.conflictListeners) l(snap)
	}

	private async emitProgress(): Promise<void> {
		if (!this.cfg.onProgress) return
		this.cfg.onProgress(await this.store.counts())
	}

	private readonly handleVisibility = (): void => {
		const doc = (globalThis as { document?: { visibilityState?: string } })
			.document
		if (doc?.visibilityState === 'visible') {
			// Resume: re-probe connectivity (radios may have slept) then drain.
			void this.detector.forceProbe().then(() => {
				if (this.running && this.detector.online) void this.flushNow()
			})
		}
		// hidden → we simply stop initiating new drains; in-flight requests
		// complete naturally and the SW Background Sync (if registered) covers
		// the tab-closed case.
	}

	private bindVisibility(): void {
		if (this.visibilityBound) return
		const target = globalThis as unknown as {
			document?: { addEventListener?: (t: string, l: () => void) => void }
		}
		target.document?.addEventListener?.('visibilitychange', this.handleVisibility)
		this.visibilityBound = true
	}

	private unbindVisibility(): void {
		if (!this.visibilityBound) return
		const target = globalThis as unknown as {
			document?: { removeEventListener?: (t: string, l: () => void) => void }
		}
		target.document?.removeEventListener?.(
			'visibilitychange',
			this.handleVisibility,
		)
		this.visibilityBound = false
	}
}
